import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box,
    ButtonBase,
    CircularProgress,
    FormControl,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { toast } from 'react-toastify'
import { useAppDispatch, useUser } from '../../hooks'
import {
    APARTMENT,
    axiosInstance,
    currencyFormatter,
    VENDOR,
} from '../../helpers'
import { WorkSpaceUser } from '../../models'
import { setWorkSpaceUser } from '../../store'
import { Container } from '../../components'
import { InvoiceRoot } from './InvoiceRoot'
import {
    PropertyInvoiceBreakdownModal,
    PropertyInvoiceSummaryRow,
} from './PropertyInvoiceBreakdownModal'

const BRAND_TEAL = '#008C85'

type DatePreset =
    | 'all'
    | 'this_week'
    | 'last_week'
    | 'month_to_date'
    | 'this_month'
    | 'last_month'
    | 'last_quarter'
    | 'this_year'
    | 'last_year'

const DATE_PRESET_LABELS: Record<DatePreset, string> = {
    all: 'All time',
    this_week: 'This week',
    last_week: 'Last week',
    month_to_date: 'Month to date',
    this_month: 'This month',
    last_month: 'Last month',
    last_quarter: 'Last quarter',
    this_year: 'This year',
    last_year: 'Last year',
}

const DATE_PRESET_ORDER: DatePreset[] = [
    'this_week',
    'last_week',
    'month_to_date',
    'this_month',
    'last_month',
    'last_quarter',
    'this_year',
    'last_year',
    'all',
]

function toISODate(d: Date): string {
    return d.toISOString().slice(0, 10)
}

function dateRangeForPreset(
    preset: DatePreset,
): { greater_than_date?: string; less_than_date?: string } {
    if (preset === 'all') return {}

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (preset) {
        case 'this_week': {
            const day = today.getDay()
            const from = new Date(today)
            from.setDate(from.getDate() - (day === 0 ? 6 : day - 1))
            return {
                greater_than_date: toISODate(from),
                less_than_date: toISODate(today),
            }
        }
        case 'last_week': {
            const from = new Date(today)
            from.setDate(from.getDate() - 7)
            return {
                greater_than_date: toISODate(from),
                less_than_date: toISODate(today),
            }
        }
        case 'month_to_date': {
            const from = new Date(today.getFullYear(), today.getMonth(), 1)
            return {
                greater_than_date: toISODate(from),
                less_than_date: toISODate(today),
            }
        }
        case 'this_month': {
            const from = new Date(today.getFullYear(), today.getMonth(), 1)
            const to = new Date(today.getFullYear(), today.getMonth() + 1, 0)
            return {
                greater_than_date: toISODate(from),
                less_than_date: toISODate(to),
            }
        }
        case 'last_month': {
            const from = new Date(today)
            from.setMonth(from.getMonth() - 1)
            return {
                greater_than_date: toISODate(from),
                less_than_date: toISODate(today),
            }
        }
        case 'last_quarter': {
            const from = new Date(today)
            from.setMonth(from.getMonth() - 3)
            return {
                greater_than_date: toISODate(from),
                less_than_date: toISODate(today),
            }
        }
        case 'this_year': {
            const from = new Date(today.getFullYear(), 0, 1)
            return {
                greater_than_date: toISODate(from),
                less_than_date: toISODate(today),
            }
        }
        case 'last_year': {
            const from = new Date(today.getFullYear() - 1, 0, 1)
            const to = new Date(today.getFullYear(), 0, 1)
            return {
                greater_than_date: toISODate(from),
                less_than_date: toISODate(to),
            }
        }
        default:
            return {}
    }
}

const parseSummaryRows = (data: unknown): PropertyInvoiceSummaryRow[] => {
    const raw = (data as { properties?: unknown })?.properties
    const arr = Array.isArray(raw) ? raw : []
    return arr.map((row: Record<string, unknown>) => ({
        apartment_id: Number(row.apartment_id) || 0,
        name: String(row.name ?? ''),
        invoice_count: Number(row.invoice_count) || 0,
        total_cost: Number(row.total_cost) || 0,
    }))
}

export const OrganizationInvoiceHome = () => {
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const { workspaceUser, rootUser, organization } = useUser()

    const [datePreset, setDatePreset] = useState<DatePreset>('all')
    const [portfolioLoading, setPortfolioLoading] = useState(false)
    const [portfolioTotalCost, setPortfolioTotalCost] = useState<number | null>(
        null,
    )
    const [portfolioInvoiceCount, setPortfolioInvoiceCount] = useState<
        number | null
    >(null)
    const [breakdownRows, setBreakdownRows] = useState<
        PropertyInvoiceSummaryRow[]
    >([])
    const [breakdownOpen, setBreakdownOpen] = useState(false)
    const [breakdownLoading, setBreakdownLoading] = useState(false)

    const workspaceOptions = useMemo(() => {
        if (!rootUser?.workspaces) return []
        return rootUser.workspaces.filter((ws) => {
            const companyType = ws.active_workspace?.company_type
            if (companyType === VENDOR) return false
            if (companyType !== APARTMENT) return false
            if (organization != null) {
                return ws.active_workspace.organization === organization
            }
            return true
        })
    }, [rootUser?.workspaces, organization])

    const selectedPropertyOption: WorkSpaceUser | undefined = useMemo(() => {
        if (!workspaceUser?.active_workspace) return undefined
        return (
            workspaceOptions.find(
                (ws) =>
                    ws.active_workspace.id ===
                    workspaceUser.active_workspace.id,
            ) ?? undefined
        )
    }, [workspaceOptions, workspaceUser?.active_workspace])

    useEffect(() => {
        if (organization == null) {
            setPortfolioTotalCost(null)
            setPortfolioInvoiceCount(null)
            setBreakdownRows([])
            return
        }

        let cancelled = false
        setPortfolioLoading(true)
        const params = { organization, ...dateRangeForPreset(datePreset) }

        Promise.all([
            axiosInstance.get('invoice/portfolio-totals/', { params }),
            axiosInstance.get('invoice/properties-invoice-summary/', {
                params,
            }),
        ])
            .then(([totalsRes, summaryRes]) => {
                if (cancelled) return
                const raw = totalsRes.data
                const cost =
                    typeof raw.total_cost === 'number'
                        ? raw.total_cost
                        : parseFloat(raw.total_cost) || 0
                setPortfolioTotalCost(cost)
                setPortfolioInvoiceCount(Number(raw.invoice_count) || 0)
                setBreakdownRows(parseSummaryRows(summaryRes.data))
            })
            .catch(() => {
                if (!cancelled) {
                    toast.error('Could not load portfolio summary')
                    setBreakdownRows([])
                }
            })
            .finally(() => {
                if (!cancelled) setPortfolioLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [organization, datePreset])

    const openBreakdown = useCallback(() => {
        setBreakdownOpen(true)
    }, [])

    const closeBreakdown = useCallback(() => {
        setBreakdownOpen(false)
    }, [])

    const handlePropertyChange = (
        _event: React.ChangeEvent<{}>,
        newValue: WorkSpaceUser | null,
    ) => {
        if (newValue) {
            dispatch(setWorkSpaceUser(newValue))
            toast.success(`Switched to ${newValue.active_workspace.name}`)
        }
    }

    return (
        <Container
            direction="column"
            flex={1}
            style={{
                height: 'calc(100vh - 104px)',
                width: 'calc(100vw - 184px)',
            }}
        >
            {workspaceUser?.active_workspace && (
                <Container
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{
                        gap: theme.spacing(2),
                        padding: theme.spacing(1.5, 2, 1),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        style={{ minWidth: 0 }}
                    >
                        {workspaceOptions.length > 1 ? (
                            <Autocomplete
                                disableClearable
                                id="ezfolio-invoices-property"
                                options={workspaceOptions}
                                getOptionLabel={(option) =>
                                    option.active_workspace?.name ?? ''
                                }
                                value={selectedPropertyOption}
                                onChange={handlePropertyChange}
                                style={{ minWidth: 300, maxWidth: 420 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Property"
                                        variant="outlined"
                                        margin="dense"
                                        size="small"
                                        placeholder="Search"
                                    />
                                )}
                            />
                        ) : (
                            <TextField
                                label="Property"
                                value={workspaceUser.active_workspace.name}
                                variant="outlined"
                                margin="dense"
                                size="small"
                                InputProps={{ readOnly: true }}
                                style={{ minWidth: 300, maxWidth: 420 }}
                            />
                        )}
                    </Box>

                    {organization != null && (
                        <Paper
                            elevation={0}
                            style={{
                                borderRadius: 8,
                                border: `1px solid ${theme.palette.divider}`,
                                borderLeft: `3px solid ${BRAND_TEAL}`,
                                backgroundColor: '#fff',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                                padding: theme.spacing(1.25, 1.75),
                                flexShrink: 0,
                                maxWidth: '100%',
                            }}
                        >
                            {portfolioLoading ? (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{
                                        gap: theme.spacing(1.5),
                                        minHeight: 40,
                                    }}
                                >
                                    <CircularProgress
                                        size={20}
                                        thickness={4}
                                        style={{ color: BRAND_TEAL }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Loading portfolio…
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    display="flex"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    style={{
                                        gap: theme.spacing(1.5),
                                        rowGap: 10,
                                    }}
                                >
                                    <Typography
                                        variant="overline"
                                        style={{
                                            color: BRAND_TEAL,
                                            fontWeight: 700,
                                            letterSpacing: '0.12em',
                                            fontSize: 10,
                                            lineHeight: 1,
                                            marginRight: theme.spacing(0.5),
                                        }}
                                    >
                                        Portfolio
                                    </Typography>

                                    <FormControl
                                        size="small"
                                        variant="outlined"
                                    >
                                        <Select
                                            value={datePreset}
                                            onChange={(e) =>
                                                setDatePreset(
                                                    e.target
                                                        .value as DatePreset,
                                                )
                                            }
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                height: 30,
                                                borderRadius: 6,
                                                backgroundColor:
                                                    theme.palette.grey[50],
                                            }}
                                        >
                                            {DATE_PRESET_ORDER.map((key) => (
                                                <MenuItem
                                                    key={key}
                                                    value={key}
                                                    style={{ fontSize: 13 }}
                                                >
                                                    {DATE_PRESET_LABELS[key]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Tooltip title="Breakdown by property">
                                        <span>
                                            <ButtonBase
                                                focusRipple
                                                disabled={portfolioLoading}
                                                onClick={openBreakdown}
                                                aria-label="View invoice breakdown by property"
                                                style={{
                                                    borderRadius: 8,
                                                    textAlign: 'left',
                                                    display: 'block',
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    alignItems="stretch"
                                                    style={{
                                                        borderRadius: 8,
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        backgroundColor:
                                                            theme.palette
                                                                .grey[50],
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Box
                                                        px={1.5}
                                                        py={0.75}
                                                        display="flex"
                                                        flexDirection="column"
                                                        justifyContent="center"
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            style={{
                                                                fontSize: 10,
                                                                lineHeight: 1.2,
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            Properties
                                                        </Typography>
                                                        <Typography
                                                            variant="subtitle1"
                                                            component="span"
                                                            style={{
                                                                fontWeight: 700,
                                                                lineHeight: 1.3,
                                                            }}
                                                        >
                                                            {
                                                                breakdownRows.length
                                                            }
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        style={{
                                                            width: 1,
                                                            alignSelf:
                                                                'stretch',
                                                            backgroundColor:
                                                                theme.palette
                                                                    .divider,
                                                        }}
                                                    />
                                                    <Box
                                                        px={1.5}
                                                        py={0.75}
                                                        display="flex"
                                                        flexDirection="column"
                                                        justifyContent="center"
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            style={{
                                                                fontSize: 10,
                                                                lineHeight: 1.2,
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            Invoices
                                                        </Typography>
                                                        <Typography
                                                            variant="subtitle1"
                                                            component="span"
                                                            style={{
                                                                fontWeight: 700,
                                                                lineHeight: 1.3,
                                                            }}
                                                        >
                                                            {(
                                                                portfolioInvoiceCount ??
                                                                0
                                                            ).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        style={{
                                                            width: 1,
                                                            alignSelf:
                                                                'stretch',
                                                            backgroundColor:
                                                                theme.palette
                                                                    .divider,
                                                        }}
                                                    />
                                                    <Box
                                                        px={1.5}
                                                        py={0.75}
                                                        display="flex"
                                                        flexDirection="column"
                                                        justifyContent="center"
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            style={{
                                                                fontSize: 10,
                                                                lineHeight: 1.2,
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            Total cost
                                                        </Typography>
                                                        <Typography
                                                            variant="subtitle1"
                                                            component="span"
                                                            style={{
                                                                fontWeight: 700,
                                                                lineHeight: 1.3,
                                                                color: BRAND_TEAL,
                                                            }}
                                                        >
                                                            {currencyFormatter.format(
                                                                portfolioTotalCost ??
                                                                    0,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </ButtonBase>
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                        </Paper>
                    )}
                </Container>
            )}

            <InvoiceRoot />

            <PropertyInvoiceBreakdownModal
                open={breakdownOpen}
                loading={portfolioLoading && breakdownOpen}
                rows={breakdownRows}
                onClose={closeBreakdown}
            />
        </Container>
    )
}
